import type { FC, ReactNode } from 'react'
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined'

import { EmptyState, Loader, SectionCard, StatusPill, TABLE_HEADER_CELL_SX } from '~/shared/ui'
import type { Tone } from '~/shared/ui'
import { formatApiDate } from '~/shared/lib/date'
import type { PatientUser } from '~/shared/types'
import {
  useGetPersonMedications,
  type PersonMedication,
  type PharmacyLink,
} from './PersonMedicationsService'

const displayDate = (iso: string | null) => formatApiDate(iso, 'MMM d, yyyy') ?? '—'

const SOURCE: Record<'ePrescribed' | 'documented', { tone: Tone; label: string }> = {
  ePrescribed: { tone: 'info', label: 'e-Prescribed' },
  documented: { tone: 'neutral', label: 'Documented on chart' },
}

const CARD_SX = { minHeight: 0, display: 'flex', flexDirection: 'column' } as const
const CARD_BODY_SX = { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } as const
const SCROLL_SX = { flex: 1, minHeight: 0, overflow: 'auto' } as const

const SecondaryText: FC<{ variant?: 'body2' | 'caption'; children: ReactNode }> = ({
  variant = 'body2',
  children,
}) => (
  <Typography variant={variant} component="div" sx={{ color: 'text.secondary' }}>
    {children}
  </Typography>
)

const CardHeader: FC<{ title: string; caption: string }> = ({ title, caption }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      px: 2,
      pt: 1.75,
      pb: 1.25,
    }}
  >
    <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }}>{title}</Typography>
    <SecondaryText>{caption}</SecondaryText>
  </Box>
)

const HeaderCell: FC<{ label: string; width: string }> = ({ label, width }) => (
  <TableCell sx={{ ...TABLE_HEADER_CELL_SX, width }}>{label}</TableCell>
)

const EmptyRow: FC<{ colSpan: number; text: string }> = ({ colSpan, text }) => (
  <TableRow>
    <TableCell colSpan={colSpan}>
      <SecondaryText>{text}</SecondaryText>
    </TableCell>
  </TableRow>
)

const MedicationRow: FC<{ medication: PersonMedication; isDiscontinued?: boolean }> = ({
  medication,
  isDiscontinued = false,
}) => {
  const textColor = isDiscontinued ? 'text.secondary' : 'text.primary'
  const source = medication.is_e_prescribed ? SOURCE.ePrescribed : SOURCE.documented
  const sourceTone: Tone = isDiscontinued ? 'neutral' : source.tone
  const pharmacy = medication.last_pharmacy
  return (
    <TableRow data-testid={`medication-${medication.id}`}>
      <TableCell>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 1,
            rowGap: 0.5,
          }}
        >
          <Typography variant="body2" sx={{ color: textColor }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {medication.name}
            </Box>
            {medication.strength && ` ${medication.strength}`}
          </Typography>
          {isDiscontinued && <StatusPill tone="error" label="Discontinued" />}
        </Box>
        {medication.directions && (
          <SecondaryText variant="caption">{medication.directions}</SecondaryText>
        )}
      </TableCell>
      <TableCell>
        <StatusPill tone={sourceTone} label={source.label} />
      </TableCell>
      <TableCell>
        {isDiscontinued ? (
          <>
            <Typography variant="body2" sx={{ color: textColor }}>
              Stopped {displayDate(medication.discontinued_on)}
            </Typography>
            <SecondaryText variant="caption">
              Last order or fill {displayDate(medication.last_ordered_or_filled_on)}
            </SecondaryText>
          </>
        ) : (
          <Typography variant="body2">
            {displayDate(medication.last_ordered_or_filled_on)}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {pharmacy ? (
          <>
            <Typography variant="body2" sx={{ color: textColor }}>
              {pharmacy.name}
            </Typography>
            <SecondaryText variant="caption">
              {pharmacy.address_line1}, {pharmacy.city}
            </SecondaryText>
          </>
        ) : (
          <SecondaryText>None recorded</SecondaryText>
        )}
      </TableCell>
    </TableRow>
  )
}

const PharmacyRow: FC<{ link: PharmacyLink }> = ({ link }) => {
  const { pharmacy } = link
  const address = [
    pharmacy.address_line1,
    pharmacy.address_line2,
    pharmacy.city,
    `${pharmacy.state} ${pharmacy.zip_code}`,
  ]
    .filter(Boolean)
    .join(', ')
  return (
    <TableRow data-testid={`pharmacy-${link.id}`}>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {pharmacy.name}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{address}</Typography>
      </TableCell>
      <TableCell>
        {link.last_used_on ? (
          <Typography variant="body2">{displayDate(link.last_used_on)}</Typography>
        ) : (
          <StatusPill tone="neutral" label="On chart, never used" />
        )}
      </TableCell>
    </TableRow>
  )
}

/**
 * The member's synced Elation medications and pharmacies. The server splits and orders every
 * list, so the tab renders the active medications, then a Discontinued group, then the pharmacies,
 * exactly as they arrive. Each card scrolls on its own; medications get most of the height.
 */
const Medications: FC<{ patient: PatientUser }> = ({ patient }) => {
  const {
    data,
    isPending: isLoading,
    error,
  } = useGetPersonMedications({ personId: patient.person?.id })

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Loader inline />
      </Box>
    )
  }

  if (error || !data) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="Couldn't load medications"
        caption="Something went wrong fetching this member's medications. Please try again."
      />
    )
  }

  const { active_medications: active, discontinued_medications: discontinued, pharmacies } = data
  const medicationCount = active.length + discontinued.length

  if (medicationCount + pharmacies.length === 0) {
    return (
      <EmptyState
        icon={<MedicationOutlinedIcon />}
        title="No medications or pharmacies"
        caption="Nothing has synced from this member's Elation chart. A member with no Elation chart has none."
      />
    )
  }

  return (
    // The same fixed pane height as the ZAP tab, so the two cards can split it.
    <Box
      sx={{
        px: 3,
        pt: 2,
        pb: 3,
        height: '78vh',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <SecondaryText>
        From the member&apos;s Elation chart. Synced daily, so today&apos;s changes show tomorrow.
      </SecondaryText>
      <SectionCard disablePadding sx={{ ...CARD_SX, flex: '1 1 0' }} bodySx={CARD_BODY_SX}>
        <CardHeader
          title="Medications"
          caption={`${active.length} active, ${discontinued.length} discontinued`}
        />
        <Box sx={SCROLL_SX}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <HeaderCell label="Medication" width="38%" />
                <HeaderCell label="Source" width="20%" />
                <HeaderCell label="Last ordered or filled" width="20%" />
                <HeaderCell label="Pharmacy" width="22%" />
              </TableRow>
            </TableHead>
            <TableBody>
              {active.map(medication => (
                <MedicationRow key={medication.id} medication={medication} />
              ))}
              {medicationCount === 0 && <EmptyRow colSpan={4} text="No medications on the chart" />}
              {discontinued.length > 0 && (
                <TableRow data-testid="discontinued-group">
                  <TableCell
                    colSpan={4}
                    sx={{ ...TABLE_HEADER_CELL_SX, bgcolor: 'background.cardFooter' }}
                  >
                    Discontinued
                  </TableCell>
                </TableRow>
              )}
              {discontinued.map(medication => (
                <MedicationRow key={medication.id} medication={medication} isDiscontinued />
              ))}
            </TableBody>
          </Table>
        </Box>
      </SectionCard>
      <SectionCard
        disablePadding
        sx={{ ...CARD_SX, flex: '0 0 auto', maxHeight: '40%' }}
        bodySx={CARD_BODY_SX}
      >
        <CardHeader title="Pharmacies" caption="Most recent use first" />
        <Box sx={SCROLL_SX}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <HeaderCell label="Pharmacy" width="34%" />
                <HeaderCell label="Address" width="42%" />
                <HeaderCell label="Last used" width="24%" />
              </TableRow>
            </TableHead>
            <TableBody>
              {pharmacies.map(link => (
                <PharmacyRow key={link.id} link={link} />
              ))}
              {pharmacies.length === 0 && <EmptyRow colSpan={3} text="No pharmacies on file" />}
            </TableBody>
          </Table>
        </Box>
      </SectionCard>
    </Box>
  )
}

export default Medications
